/**
 * One figure from a report row: a number, or a named absence and never a number.
 *
 * `web/src/reports/figure.ts` does the deciding; this renders the two branches. The
 * split matters because the absent branch has **no numeric member to reach for** —
 * a component that wanted to print `0` for an absence would have to invent the zero
 * itself, and a reviewer would see it being invented.
 *
 * The absent branch prints the sentinel **verbatim** — `#NOT_COMPUTED`, `#` and all —
 * beside a sentence saying what it means. Two reasons for the literal rather than a
 * translated phrase:
 *
 *  1. It is the same eight characters the reader will meet in the generated file, so
 *     the legend on this page is demonstrably about the thing in their spreadsheet.
 *  2. It is unparseable as a number in Excel, LibreOffice, `pandas.read_csv` and
 *     `awk`, which is exactly why Task 46 chose it — and a UI that softened it to
 *     "chưa có" would be re-opening the confusion at the only point where the person
 *     is looking at both the record and the file.
 *
 * `data-absence` carries the kind for the tests, so a mutation that swaps one absence
 * for another dies on the attribute rather than needing four separate sentences to be
 * grepped out of the DOM.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { ABSENCE_TEXT, type ReportFigure } from '../../reports/figure';
import type { MessageKey } from '../../i18n/messages';

export interface FigureValueProps {
  readonly figure: ReportFigure;
  readonly testId: string;
}

export function FigureValue({ figure, testId }: FigureValueProps): ReactNode {
  const { t } = useI18n();
  if (figure.kind === 'measured') {
    return (
      <span data-testid={testId} data-measured="true">
        {figure.value}
      </span>
    );
  }
  const sentinel = ABSENCE_TEXT[figure.absence];
  const meaning = t(`k8.reports.absence.${figure.absence}` as MessageKey);
  return (
    <span
      className="wp-figure--words"
      data-testid={testId}
      data-absence={figure.absence}
      title={meaning}
    >
      <code className="wp-mono">{sentinel}</code>{' '}
      <span className="wp-small wp-muted">{meaning}</span>
    </span>
  );
}
